import { APIDateFilter, DateRange } from "@/types/Date";
import { format, FormatOptions, fromUnixTime, parseISO } from "date-fns";
import { isDateRange as isDateRangeCheck } from "react-day-picker";

export enum DateFormat {
  // Numeric formats
  SHORT = "dd.MM.yyyy", // 24.04.2025
  SHORT_WITH_TIME = "dd.MM.yyyy HH:mm", // 24.04.2025 14:30
  SHORT_SLASH = "dd/MM/yyyy", // 24/04/2025
  SHORT_WITH_TIME_SLASH = "dd/MM/yyyy HH:mm", // 24/04/2025 14:30
  SHORT_DASH = "dd-MM-yyyy", // 24-04-2025
  SHORT_DASH_WITH_TIME = "dd-MM-yyyy HH:mm", // 24-04-2025 14:30

  // With month names
  LONG = "dd MMMM yyyy", // 24 April 2025
  LONG_WITH_TIME = "dd MMMM yyyy HH:mm", // 24 April 2025 14:30

  // With weekday
  FULL = "EEEE, dd MMMM yyyy", // Thursday, 24 April 2025
  FULL_WITH_TIME = "EEEE, dd MMMM yyyy HH:mm", // Thursday, 24 April 2025 14:30
  WEEKDAY_DAY_MONTH = "EEEE d MMMM", // Saturday 14 June
  FULL_WITH_SLASH = "EEEE, dd/MM/yyyy", // Thursday, 24/April/2025
  SHORT_WEEKDAY_SLASH = "EEE, dd/MM/yyyy", // Thu, 24/April/2025

  // Special formats
  ISO = "yyyy-MM-dd", // 2025-04-24 (ISO 8601)
  ISO_WITH_TIME = "yyyy-MM-dd'T'HH:mm:ss.SSSxxx", // 2025-04-24T14:30:00.000+00:00

  // Time only
  TIME_24H = "HH:mm", // 14:30
  TIME_12H = "hh:mm a", // 02:30 PM

  // Month and year only
  MONTH_YEAR = "MMMM yyyy", // April 2025
  SHORT_MONTH_YEAR = "MMM yyyy", // Apr 2025
}

/**
 * Utility function to normalize different date inputs into a Date object
 */
export const normalizeDate = (date: Date | string | number): Date => {
  if (typeof date === "string") {
    // For ISO strings like "2024-01-15T10:30:00Z"
    return parseISO(date);
  } else if (typeof date === "number") {
    // For Unix timestamps
    return fromUnixTime(date);
  } else {
    // Already a Date object
    return date;
  }
};

/**
 * Formats date/time in UTC
 */
export const formatDate = (
  date: Date | string | number | undefined,
  dateFormat = DateFormat.SHORT_SLASH,
  options?: FormatOptions,
  showTime = false
): string => {
  if (!date) return "";
  const parsedDate = normalizeDate(date);
  const newFormat = showTime ? dateFormat + " HH:mm" : dateFormat;
  return format(parsedDate, newFormat, options);
};

/**
 * Type guard to determine if a date value is a date range object.
 * Returns true if the date has 'from' property (indicating a range), false for single dates or undefined.
 */
export const isDateRange = isDateRangeCheck;

/**
 * Extracts a single date from a date value.
 * Returns the date if it's a single date, or the 'from' date if it's a range.
 * Returns undefined if the input is undefined or if you want strict single-date extraction.
 */
export const getSingleDate = (
  date: Date | DateRange | undefined,
  strict: boolean = false
): Date | undefined => {
  if (!date) return undefined;

  if (isDateRange(date)) {
    return strict ? undefined : date.from;
  }

  return date;
};

/**
 * Extracts a date range from a date value.
 * Returns the range if it's already a range, or undefined for single dates.
 */
export const getRangeDate = (
  date: Date | DateRange | undefined
): DateRange | undefined => {
  return isDateRange(date) ? date : undefined;
};

/**
 * Converts a single date to a date range (from date to same date).
 * Useful when you need to normalize single dates to ranges.
 */
export const toDateRange = (
  date: Date | DateRange | undefined
): DateRange | undefined => {
  if (!date) return undefined;

  if (isDateRange(date)) {
    return date;
  }

  return { from: date, to: date };
};

/**
 * Gets the start date from either a single date or date range.
 */
export const getStartDate = (
  date: Date | DateRange | undefined
): Date | undefined => {
  if (!date) return undefined;
  return isDateRange(date) ? date.from : date;
};

/**
 * Gets the end date from either a single date or date range.
 * For single dates, returns the same date.
 */
export const getEndDate = (
  date: Date | DateRange | undefined
): Date | undefined => {
  if (!date) return undefined;
  return isDateRange(date) ? date.to || date.from : date;
};

/**
 * Converts a date or date range into an array of dates.
 * For single dates, returns an array with one element.
 * For date ranges, returns an array with from and to dates (filtering out undefined).
 * Returns an empty array if the input is undefined.
 */
export const getDateList = (date: Date | DateRange | undefined): Date[] => {
  if (!date) return [];

  if (!isDateRange(date)) {
    return [date];
  }

  const dates: Date[] = [];
  if (date.from) dates.push(date.from);
  if (date.to) dates.push(date.to);
  return dates;
};

/**
 * Converts a date or date range to an API-compatible date filter format.
 * For date ranges, returns both startDate and endDate in ISO format.
 * For single dates, returns only endDate in ISO format.
 * Returns an empty object if the input is undefined.
 */
export const getApiDateFilter = (
  date: Date | DateRange | undefined
): APIDateFilter => {
  if (!date) return {};

  if (isDateRange(date)) {
    return {
      startDate: formatDate(date.from, DateFormat.ISO, {}, true),
      endDate: formatDate(date.to, DateFormat.ISO, {}, true),
    };
  }

  return {
    endDate: formatDate(date, DateFormat.ISO, {}, true),
  };
};

// Date pickers default end-of-range time to HH:MM:00.000.
// Bump to HH:MM:59.999 so the API filter is inclusive of the selected minute.
const inclusiveEndOfMinute = (date: Date | undefined): Date | undefined => {
  if (!date) return date;
  const d = new Date(date);
  d.setSeconds(59, 999);
  return d;
};

export const getApiDateFilterBackend = (
  date: Date | DateRange | undefined
): any => {
  if (!date) return {};

  if (isDateRange(date)) {
    return {
      startDate: date.from,
      endDate: inclusiveEndOfMinute(date.to),
    };
  }

  return {
    endDate: inclusiveEndOfMinute(date),
  };
};
